import { CloudDownload, Image, MoreVertical, Trash2 } from 'lucide-react';
import { Button } from 'src/ui/button';
import { Typography } from 'src/ui/typography';
import { Alert } from 'src/ui/alert';
import ProgressBar from 'src/ui/progress-bar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/ui/dropdown-menu';
import React, { useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import axios from 'axios';
import find from 'lodash/find';

import { useDropzone, FileRejection } from 'react-dropzone';
import { SignedUrl } from 'src/utils/google/storage';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from 'src/ui/carousel';
import { cn } from 'src/utils/@/lib/utils';
import { trpc } from 'src/utils/trpc';
import { inferProcedureOutput } from '@trpc/server';
import { AppRouter } from 'src/server/trpc/router';
import { ConfirmDialog } from '../components/form/ConfirmDialog';
import { Thumbnails } from './Thumbnails';
import { window_open } from 'src/utils/flutter';
import toast from 'react-hot-toast';

export type UploadToStorageProps = {
  files: Array<File>;
  onUploadProgress: ((progressEvent: any) => void) | undefined;
  path: string;
  bucketName: string;
};

export const uploadToStorage = async ({ files, onUploadProgress, path, bucketName }: UploadToStorageProps) => {
  const mappedFiles = files.map((file) => ({
    filename: file.name,
    bucket: bucketName,
    path,
    content_type: file.type,
  }));

  const response = await axios.post('/api/google/storage/signed-urls', {
    files: mappedFiles,
  });

  const signedUrlResults = response.data;
  const signedUrls = signedUrlResults.signedUrls as Array<SignedUrl>;

  await Promise.all(
    signedUrls.map(async (signedUrl) => {
      const file = find(files, (file) => file.name === signedUrl.originalname);
      return await axios.put(signedUrl.signed_url, file, {
        headers: { 'Content-Type': signedUrl.content_type },
        onUploadProgress,
      });
    })
  );

  const fileNames = signedUrls.map((signedUrl) => signedUrl);

  return fileNames;
};

type DropzoneProps = {
  addImage: (files: Array<SignedUrl>) => void;
  allowedFiles: any;
};

type Message = {
  open: boolean;
  message: string;
  type?: 'success' | 'error';
};

const Dropzone: React.FC<DropzoneProps> = ({ addImage, allowedFiles }) => {
  const [percentCompleted, setPercentCompleted] = useState(0);
  const [isUploadCompleted, setIsUploadCompleted] = useState(false);

  const [openMessage, setOpenMessage] = useState<Message>({
    open: false,
    message: '',
    type: 'success',
  });

  const onUploadProgress = (progressEvent: any) => {
    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    setPercentCompleted(percent);
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        setOpenMessage({
          open: true,
          message: `Removed ${fileRejections.length} invalid file formats.`,
          type: 'error',
        });
      }
      setPercentCompleted(0);
      setIsUploadCompleted(false);
      const results = await uploadToStorage({
        files: acceptedFiles,
        onUploadProgress,
        path: 'attachments',
        bucketName: 'carketa-next-attachments',
      });
      addImage(results);
      setIsUploadCompleted(true);
      // eslint-disable-next-line
    },
    // eslint-disable-next-line
    []
  );

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept: allowedFiles
      ? allowedFiles
      : {
          'image/jpeg': [],
          'image/png': [],
          'text/csv': [],
          'application/msword': [],
          'application/pdf': [],
          'application/json': [],
        },
  });

  const dropzoneClassName = cn(
    'flex-1 flex flex-col items-center p-5 border-2 rounded-[2px] border-dashed bg-[#8A8D93] text-primary-foreground outline-none transition-[border] [transition-duration:240ms] ease-in-out',
    isFocused ? 'border-[#2196f3]' : 'border-[#c7c9cb]',
    isDragAccept && 'border-[#00e676]',
    isDragReject && 'border-[#ff1744]'
  );

  const handleCloseMessage = () => {
    setOpenMessage({
      open: false,
      message: '',
    });
  };

  useEffect(() => {
    if (!openMessage.open) return;
    const timeout = setTimeout(handleCloseMessage, 3000);
    return () => clearTimeout(timeout);
  }, [openMessage.open]);

  const extensions = allowedFiles ? Object.keys(allowedFiles).map((key) => key.toUpperCase().split('/').pop()) : null;

  return (
    <>
      {openMessage.open && (
        <div className="fixed top-4 right-4 z-[10000]">
          <Alert
            elevation={3}
            severity={openMessage.type === 'error' ? 'error' : 'success'}
          >
            {openMessage.message}
          </Alert>
        </div>
      )}
      <div className="py-4">
        <div {...getRootProps({ className: dropzoneClassName })}>
          <input {...getInputProps()} />
          <div className="hidden sm:block text-center">
            <p>Drag &apos;n&apos; drop some files here, or click to select files </p>
            {extensions && <p>({extensions.join(', ')})</p>}
          </div>
          <div className="block sm:hidden text-center">
            <p>Click here to select file or access camera</p>
            {extensions && <p>({extensions.join(', ')})</p>}
          </div>
        </div>
      </div>
      {isUploadCompleted ? null : <ProgressBar value={percentCompleted} showLabel={false} />}
    </>
  );
};

export type CloudFile = {
  bucket: string;
  content_type: string;
  ext: string;
  file: string;
  filename: string;
  test: string;
  originalname: string;
};

interface Props {
  id: string;
  setMainUrlEnabled?: boolean;
  showThumbnails: boolean;
  allowUpload: boolean;
  showPlaceholder: boolean;
  showMenu: boolean;
  pdfMode?: boolean;
  allowedFiles?: any;
}
export type Attachments = inferProcedureOutput<AppRouter['attachments']['getByKey']>;

export const FileViewer: React.FC<Props> = ({
  id,
  setMainUrlEnabled,
  showThumbnails,
  allowUpload,
  showMenu,
  showPlaceholder = true,
  pdfMode = false,
  allowedFiles = null,
}) => {
  const [selected, setSelected] = useState({} as any);
  const [openDelete, setOpenDelete] = useState(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);

  const queryClient = useQueryClient();
  const updateVehicleCard = getQueryKey(trpc.vehicles.getById);

  const { data: attachments, refetch } = trpc.attachments.getByKey.useQuery(
    { key: id },
    { placeholderData: (previousData) => previousData, refetchOnWindowFocus: false, enabled: !!id }
  );

  useEffect(() => {
    if (attachments?.length && !selected?.id) {
      setSelected(attachments[0]);
    }
  }, [attachments, selected?.id]);

  useEffect(() => {
    if (!carouselApi || !attachments?.length) {
      return;
    }

    const onSelect = () => {
      const index = carouselApi.selectedScrollSnap();
      setCurrentSlide(index);
      setSelected(attachments[index]);
    };

    onSelect();
    carouselApi.on('select', onSelect);

    return () => {
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi, attachments]);

  useEffect(() => {
    if (!carouselApi || !attachments?.length || !selected?.id) {
      return;
    }

    const index = attachments.findIndex((attachment) => attachment.id === selected.id);
    if (index >= 0 && carouselApi.selectedScrollSnap() !== index) {
      carouselApi.scrollTo(index);
    }
  }, [carouselApi, attachments, selected?.id]);

  const createAttachmentMutation = trpc.attachments.create.useMutation({
    onError(error) {
      console.error(error);
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: updateVehicleCard });
      refetch();
    },
  });

  const updateVehicleImageMutation = trpc.vehicles.update.useMutation({
    onError(error) {
      console.error(error);
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: updateVehicleCard });
    },
  });

  const deletePlaceholderMutation = trpc.vehicles.deleteAppraisalImage.useMutation({
    onError(error) {
      console.error(error);
      toast.error(error.message || 'Failed to update the default picture');
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: updateVehicleCard });
      refetch();
    },
  });

  const onAddImage = (files: Array<SignedUrl>) => {
    files.forEach((file) => {
      createAttachmentMutation.mutate({ key: id, attachment: file });
    });
  };

  const updateAttachmentMutation = trpc.attachments.update.useMutation({
    onError(error) {
      console.error(error);
      // Was silent, so a failed delete looked identical to a no-op.
      toast.error(error.message || 'Failed to remove the file');
    },
    onSuccess() {
      toast.success('File removed');
      refetch();
    },
  });

  /**
   * Close the dropdown menu *before* opening the confirm dialog.
   *
   * Two Radix focus traps on screen at once ping-pong on focusout until the call
   * stack blows ("Maximum call stack size exceeded"), and the overflow
   * aborts the React event dispatch, so the delete never fired.
   */
  const onDeleteClick = () => {
    setOpenDelete(true);
  };

  const onImageDelete = () => {
    const target = selected;
    if (!target?.id || !target?.payload) {
      toast.error('Could not tell which file to remove. Reopen the menu and try again.');
      return;
    }

    // Point at a surviving file, not the row we just removed. The seed effect
    // only fires when nothing is selected, so it will not correct a stale id.
    const remaining = (attachments ?? []).filter((a) => a.id !== target.id);

    updateAttachmentMutation.mutate({ id: target.id });
    deletePlaceholderMutation.mutate({
      id: id,
      url: target.payload.attachment.file,
    });
    setSelected(remaining[0] ?? ({} as any));
  };

  const fileMenu = (attachment?: any) => {
    const target = attachment ?? selected;
    const ext = target?.payload?.attachment?.ext;
    const isImageExt =
      ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gif';
    return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="contained"
          color="secondary"
          size="sm"
          className={showMenu ? '' : 'hidden'}
          onClick={() => {
            if (attachment) setSelected(attachment);
          }}
        >
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[8rem]">
        <DropdownMenuItem onClick={() => onDeleteClick()}>
          <Trash2 className="mr-[10px] dark:text-white" />
          Delete
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            window_open(target.payload.attachment.file, '_blank', 'noreferrer');
          }}
        >
          <CloudDownload className="mr-[10px] dark:text-white" />
          Download File
        </DropdownMenuItem>
        {setMainUrlEnabled && target && isImageExt && (
            <DropdownMenuItem
              onClick={() => {
                updateVehicleImageMutation.mutate({
                  id: id,
                  url: target.payload.attachment.file,
                });
              }}
            >
              <Image className="mr-[10px] dark:text-white" />
              Set Default Picture
            </DropdownMenuItem>
          )}
      </DropdownMenuContent>
    </DropdownMenu>
    );
  };

  const firstFile = attachments ? (attachments[0]?.payload as { attachment: CloudFile }) : null;
  return (
    <div className="w-full min-w-0 max-w-full">
      {attachments && attachments.length > 0 ? (
        <>
          {pdfMode ? (
            <>
              <div className="flex justify-center items-center flex-col mb-6  xs sm md">
                <div
                  className="Media h-[400px] w-[600px] max-w-full bg-no-repeat bg-center"
                  style={{
                    backgroundImage: `url(${firstFile?.attachment?.file || ''})`,
                    backgroundSize: 'contain',
                  }}
                  title={firstFile?.attachment?.originalname || ''}
                >
                  <div className="flex justify-end p-4 z-[12] w-3/4 sm:w-[95%]"></div>
                </div>
              </div>
            </>
          ) : (
            <Carousel
              setApi={setCarouselApi}
              opts={{ loop: false }}
              className="w-full min-w-0 max-w-full"
            >
              <CarouselContent className="-ml-0">
                {attachments.map((attachment, i) => {
                  const payload = attachment?.payload as {
                    attachment: CloudFile;
                  };
                  const isImage =
                    payload.attachment.ext === 'jpeg' ||
                    payload.attachment.ext === 'png' ||
                    payload.attachment.ext === 'jpg' ||
                    payload.attachment.ext === 'gif';

                  return (
                    <CarouselItem key={attachment.id ?? i} className="pl-0">
                      {!isImage ? (
                        <div className="flex justify-center relative min-h-[300px] sm:min-h-[315px] md:min-h-[400px] items-center">
                          <div className="flex justify-end items-center w-1/2 sm:w-full absolute top-0 z-[12]">
                            {fileMenu(attachment)}
                          </div>
                          <span
                            className={`fiv-viv fiv-icon-${payload.attachment.ext} fiv-size-lg`}
                            style={{ fontSize: '150px' }}
                          ></span>
                          <Typography className="mt-2 absolute bottom-0">
                            {payload.attachment.originalname}
                          </Typography>
                        </div>
                      ) : (
                        <div className="flex justify-center items-center h-[300px] sm:h-[315px] md:h-[400px] flex-col min-w-0">
                          <div
                            className="relative h-full w-full max-w-[600px] bg-contain bg-center bg-no-repeat"
                            style={{
                              backgroundImage: `url(${payload.attachment.file || ''})`,
                            }}
                            title={payload.attachment.originalname || ''}
                          >
                            <div className="flex justify-end p-4 z-[12] w-3/4 sm:w-[95%]">
                              {fileMenu(attachment)}
                            </div>
                          </div>
                        </div>
                      )}
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious className="left-2 top-[40%] z-10" />
              <CarouselNext className="right-2 top-[40%] z-10" />
              <div className="mt-2.5 flex w-full flex-row flex-wrap items-center justify-center gap-1">
                {attachments.map((attachment, i) => (
                  <button
                    key={attachment.id ?? i}
                    type="button"
                    aria-label={`Go to slide ${i + 1}`}
                    className={cn(
                      'rounded-full p-0 transition-opacity',
                      currentSlide === i ? 'h-2.5 w-2.5 opacity-100' : 'h-2 w-2 opacity-50'
                    )}
                    onClick={() => carouselApi?.scrollTo(i)}
                  >
                    <span className="block h-full w-full rounded-full bg-current" />
                  </button>
                ))}
              </div>
            </Carousel>
          )}
        </>
      ) : showPlaceholder ? (
        <div className="flex justify-center items-center h-[400px] flex-col">
          <img
            id="file-viewer"
            className="Media h-[400px] w-[400px] object-contain"
            src={'/img/car_new_arrival.jpg'}
            alt={'/img/car_new_arrival.jpg'}
          />
        </div>
      ) : (
        <></>
      )}
      {showThumbnails ? (
        <Thumbnails
          selected={selected}
          onThumbnailSelect={(attachment) => {
            setSelected(attachment);
          }}
          attachments={attachments || []}
        />
      ) : null}
      {allowUpload ? (
        <div className="py-4">
          <Dropzone addImage={onAddImage} allowedFiles={allowedFiles} />
        </div>
      ) : null}
      {openDelete && (
        <ConfirmDialog
          title="Remove?"
          description={`Are you sure you want to remove this file?`}
          open={openDelete}
          handleClose={() => setOpenDelete(false)}
          handleOk={() => onImageDelete()}
        />
      )}
    </div>
  );
};
